'use client'

import { ComponentProps, useEffect, useRef, useState } from 'react'

import { flexRender } from '@tanstack/react-table'

import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { cn } from '@/lib/cn'
import { DataTablePagination } from './data-table-pagination'
import { useDataTableContext } from './data-table-root'

export function DataTableTable({
  children,
  className,
  ...props
}: ComponentProps<'div'>) {
  const { table, isLoading, rowCount } = useDataTableContext()

  const [showStickyBorder, setShowStickyBorder] = useState(false)
  const scrollableRef = useRef<HTMLDivElement>(null)

  const { pageIndex } = table.getState().pagination
  const columns = table.getAllColumns()
  const columnsCount = columns.length

  const hasNumberedColumns = columns.find((c) => c.id === 'no')
  const hasSelect = columns.find((c) => c.id === 'select')
  const hasActions = columns.find((c) => c.id === 'action')

  const rows = table.getRowModel().rows
  const noResult = !isLoading && rows.length === 0

  const handleHorizontalScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft

    if (scrollLeft > 0) {
      setShowStickyBorder(true)
    } else {
      setShowStickyBorder(false)
    }
  }

  useEffect(() => {
    scrollableRef.current?.scroll({ top: 0, left: 0 })
  }, [pageIndex])

  return (
    <div
      className={cn('flex flex-col gap-2.5 overflow-auto', className)}
      {...props}
    >
      {children}
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader
            className="sticky inset-x-0 top-0 z-[1] w-full border-b-0 border-t-0 shadow-[0_1px_1px_0] shadow-border"
            style={{ transform: 'translate3d(0,0,0)' }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className={cn('border-b-0', {
                  '[&_th:last-of-type]:w-[1%] [&_th:last-of-type]:whitespace-nowrap':
                    hasActions,
                  '[&_th:first-of-type]:w-[1%] [&_th:first-of-type]:whitespace-nowrap':
                    hasSelect || hasNumberedColumns,
                })}
              >
                {headerGroup.headers.map((header, idx) => {
                  const isActionHeader = header.id === 'action'
                  const isSelectHeader = header.id === 'select'
                  const isNumberedHeader = header.id === 'no'
                  const isSpecialHeader =
                    isActionHeader || isSelectHeader || isNumberedHeader

                  const isFirstColumn = hasSelect ? idx === 1 : idx === 0

                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={cn('whitespace-nowrap', {
                        'w-[calc(20px+24px+24px)] min-w-[calc(20px+24px+24px)] max-w-[calc(20px+24px+24px)]':
                          isSelectHeader,
                        'w-[calc(28px+24px+4px)] min-w-[calc(28px+24px+4px)] max-w-[calc(28px+24px+4px)]':
                          isActionHeader,
                        "after:absolute after:inset-y-0 after:right-0 after:h-full after:w-px after:bg-transparent after:content-['']":
                          isFirstColumn,
                        'after:bg-background':
                          showStickyBorder && isFirstColumn,
                        'sticky bg-background': isFirstColumn || isSelectHeader,
                        'left-0':
                          isSelectHeader || (isFirstColumn && !hasSelect),
                        'left-[calc(20px+24px+24px)]':
                          isFirstColumn && hasSelect,
                      })}
                      style={
                        !isSpecialHeader
                          ? {
                              width: header.column.columnDef.size,
                              maxWidth: header.column.columnDef.maxSize,
                              minWidth: header.column.columnDef.minSize,
                            }
                          : undefined
                      }
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading && <DataTableSkeleton columnsCount={columnsCount} />}
            {noResult && <DataTableNoResult columnsCount={columnsCount} />}

            {!isLoading &&
              !noResult &&
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn('group/row last-of-type:border-b-0')}
                >
                  {row.getVisibleCells().map((cell, idx) => {
                    const isSelectCell = cell.column.id === 'select'
                    const isActionCell = cell.column.id === 'action'
                    const isNumberedCell = cell.column.id === 'no'
                    const isSpecialCell =
                      isSelectCell || isActionCell || isNumberedCell

                    const isFirstColumn = hasSelect ? idx === 1 : idx === 0
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          'items-stretch truncate whitespace-nowrap',
                          {
                            'w-[calc(20px+24px+24px)] min-w-[calc(20px+24px+24px)] max-w-[calc(20px+24px+24px)]':
                              isSelectCell,
                            'w-[calc(28px+24px+4px)] min-w-[calc(28px+24px+4px)] max-w-[calc(28px+24px+4px)]':
                              isActionCell,
                            'bg-ui-bg-base group-hover/row:bg-ui-bg-base-hover transition-fg sticky h-full':
                              isFirstColumn || isSelectCell,
                            "bg-background after:absolute after:inset-y-0 after:right-0 after:h-full after:w-px after:bg-transparent after:content-['']":
                              isFirstColumn,
                            'after:bg-background':
                              showStickyBorder && isFirstColumn,
                            'left-0':
                              isSelectCell || (isFirstColumn && !hasSelect),
                            'left-[calc(20px+24px+24px)]':
                              isFirstColumn && hasSelect,
                          }
                        )}
                        style={
                          !isSpecialCell
                            ? {
                                width: cell.column.columnDef.size,
                                maxWidth: cell.column.columnDef.maxSize,
                                minWidth: cell.column.columnDef.minSize,
                              }
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col gap-2.5">
        <DataTablePagination />
      </div>
    </div>
  )
}

interface DataTableTableSkeletonProps {
  pageSize?: number
}

function DataTableTableSkeleton({
  pageSize = 10,
}: DataTableTableSkeletonProps) {
  return (
    <div className="flex w-full flex-1 flex-col overflow-hidden">
      <div className="min-h-0 w-full flex-1 overscroll-none border-y">
        <div className="flex flex-col divide-y">
          <Skeleton className="h-12 w-full" />
          {Array.from({ length: pageSize }, (_, i) => i).map((row) => (
            <Skeleton key={row} className="h-12 w-full rounded-none" />
          ))}
        </div>
      </div>
    </div>
  )
}

function DataTableSkeleton({ columnsCount }: { columnsCount: number }) {
  return Array.from({ length: 10 }, (_, i) => i).map((row) => (
    <TableRow key={row}>
      <TableCell colSpan={columnsCount} className="text-center">
        <Skeleton className="h-8 w-full rounded-none" />
      </TableCell>
    </TableRow>
  ))
}

function DataTableNoResult({ columnsCount }: { columnsCount: number }) {
  return (
    <TableRow>
      <TableCell colSpan={columnsCount} className="h-24 text-center">
        No results.
      </TableCell>
    </TableRow>
  )
}
